import { defineStore } from 'pinia'

import { apiRequest, configureAPIClient, getResource } from '@/api/client'
import type { Me, SessionView, TenantMembership } from '@/api/types'
import { errorMessage } from '@/api/format'

export const useSessionStore = defineStore('session', {
  state: () => ({
    ready: false,
    loading: false,
    session: null as SessionView | null,
    me: null as Me | null,
    tenants: [] as TenantMembership[],
    error: '',
  }),
  getters: {
    authenticated: (state) => state.session?.authenticated === true,
    csrf: (state) => state.session?.csrf_token,
    scopes: (state) => new Set(state.me?.principal.scopes ?? []),
    tenantName: (state) => state.me?.tenant.name ?? 'Tenant Blaze',
    operatorName: (state) => state.session?.user?.name || state.me?.principal.name || 'Operador',
  },
  actions: {
    configureClient() {
      configureAPIClient({
        csrf: () => this.csrf,
        unauthorized: () => this.expire(),
      })
    },
    async bootstrap() {
      this.loading = true
      this.error = ''
      try {
        const response = await apiRequest<SessionView>('/auth/session')
        this.session = response.data
        if (response.data.authenticated) {
          const me = await getResource<Me>('/api/v1/me')
          this.me = me.data
        } else {
          this.me = null
        }
      } catch (error) {
        this.error = errorMessage(error)
      } finally {
        this.loading = false
        this.ready = true
      }
    },
    expire() {
      this.session = { authenticated: false }
      this.me = null
      this.tenants = []
    },
    can(scope?: string) {
      if (!scope) return true
      if (this.scopes.has('*')) return true
      const requiredParts = scope.split(':')
      return [...this.scopes].some((grant) => {
        const grantParts = grant.split(':')
        return (
          grantParts.length === requiredParts.length &&
          grantParts.every((part, index) => part === '*' || part === requiredParts[index])
        )
      })
    },
    canAny(scopes: string[]) {
      return scopes.some((scope) => this.can(scope))
    },
    login(returnTo: string) {
      window.location.assign(`/auth/login?return_to=${encodeURIComponent(returnTo || '/')}`)
    },
    async logout() {
      await apiRequest<void>('/auth/logout', { method: 'POST' })
      this.expire()
      window.location.assign('/')
    },
    async loadTenants() {
      const response = await apiRequest<{ data: TenantMembership[] }>('/auth/tenants')
      this.tenants = response.data.data
    },
    async switchTenant(tenantID: string) {
      const response = await apiRequest<{ tenant_id: string; csrf_token: string; expires_at: string }>(
        '/auth/tenant',
        { method: 'POST', body: { tenant_id: tenantID } },
      )
      if (this.session) {
        this.session.tenant_id = response.data.tenant_id
        this.session.csrf_token = response.data.csrf_token
        this.session.expires_at = response.data.expires_at
      }
      const me = await getResource<Me>('/api/v1/me')
      this.me = me.data
    },
  },
})
